import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const YouTubeVideoRankContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline */}
      <Typography variant="h4" fontWeight="bold" sx={{ fontSize: "1.75rem" }} gutterBottom>
        Instantly Find Your Video's Rank in YouTube Search
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Know exactly where your video shows up — in seconds
      </Typography>

      {/* Problem/Solution */}
      <Typography variant="body1" paragraph>
        Wondering why your video isn’t getting views? If your video doesn’t show up in search, your audience can’t find it.
      </Typography>
      <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        Our Video Rank Checker shows where your video appears for any keyword in any country — so you can optimize with confidence.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Use the Video Rank Checker?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block", listStyleType: 'none', paddingLeft: 0 }}>
        <li>🔍 <strong>Precise Insights:</strong> Find your video's actual rank in YouTube search</li>
        <li>🌐 <strong>Global Data:</strong> Check how your video ranks in different countries</li>
        <li>📊 <strong>Better Strategy:</strong> Identify keywords that are working — and those that aren’t</li>
        <li>⏱️ <strong>Fast & Easy:</strong> Just paste the link, pick a keyword, and see results in seconds</li>
        <li>🚀 <strong>Boost Performance:</strong> Make informed SEO decisions to grow your reach</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How to Check Your Video's Rank in 3 Easy Steps
      </Typography>
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        p: 3, 
        borderRadius: 2,
        textAlign: 'left',
        display: 'inline-block'
      }}>
        <ol>
          <li><strong>Paste</strong> your YouTube video link</li>
          <li><strong>Enter</strong> a search keyword</li>
          <li><strong>Select</strong> a country and click "Check Video Rank"</li>
        </ol>
      </Box>

      {/* Testimonial */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Trusted by Creators Who Rank
      </Typography>
      <Box sx={{ fontStyle: 'italic', mb: 3 }}>
        <Typography paragraph>"I didn’t even realize my video was ranked #7 for my main keyword until I checked this tool. Game-changer for my channel growth!"</Typography>
        <Typography>- Mike L., Tech YouTuber (45K subs)</Typography>
      </Box>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>
      
      <Box textAlign="left" sx={{ display: 'inline-block' }}>
        <Typography variant="body1" fontWeight="bold">🎯 What does the rank number mean?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          It tells you the position where your video appears in YouTube search for a specific keyword in a selected country.
        </Typography>
        
        <Typography variant="body1" fontWeight="bold">🌎 Can I check rankings in other countries?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Absolutely. Choose from dozens of countries to see how your video performs globally.
        </Typography>
        
        <Typography variant="body1" fontWeight="bold">📅 How often can I use this tool?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          As often as you like! Each check uses one credit.
        </Typography>
      </Box>

      {/* CTA */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: 'bold' }}>
          Stop guessing where your video ranks. Start growing with data.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large" 
          onClick={scrollToTop}
          sx={{ 
            fontSize: '1.1rem',
            py: 1.5,
            px: 4,
            fontWeight: 'bold'
          }}
        >
          Check My Video Rank Now →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 credit = 1 video + keyword ranking analysis
        </Typography>
      </Box>
    </Container>
  );
};

export default YouTubeVideoRankContent;
